import { useMemo, useState, useEffect, useCallback } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { Flex, Text, Icon, Spin } from '@nula/core-ui';
import { EmbeddableCanvas } from './embeddable-canvas';
import { canvasRoots } from "@/features/canvas/engine/wiring/canvas-roots";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { CanvasContext } from './shell/canvas-context';
import { useCanvasAwareness } from './shell/hooks/use-canvas-awareness';
import { useCursorAwareness } from './shell/hooks/use-cursor-awareness';
import type { CanvasDiffResult } from '@/features/canvas/utils/canvas-diff-engine';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './canvas-viewer.module.css';

const ANONYMOUS_USER = { id: 'anonymous', name: 'Anonymous', color: 'var(--accent)' };

interface CanvasViewerProps {
  yDoc: Y.Doc;
  awareness: Awareness;
  entityId: string;
  user?: { id: string; name: string; color: string };
  diffResult?: CanvasDiffResult;
  /**
   * When true, fits the view to all nodes once they're measured. Used by the
   * diff / snapshot-restore panes so the content frames regardless of where
   * the node graph sits in flow-space. Only available in view-only mode —
   * `CanvasViewer` is itself read-only, so this never affects live editing.
   */
  fitOnMount?: boolean;
  /** When true, suppresses the zoom-controls panel. */
  hideControls?: boolean;
}

export function CanvasViewer({ yDoc, awareness, entityId, user = ANONYMOUS_USER, diffResult, fitOnMount, hideControls }: CanvasViewerProps) {
  const binding = useCanvasBinding();
  const [hasContent, setHasContent] = useState<boolean | null>(null);

  useEffect(() => {
    const nodes = canvasRoots(yDoc, binding).nodes;
    if (nodes.size > 0) {
      setHasContent(true);
      return;
    }
    setHasContent(false);
    const observer = () => {
      if (nodes.size > 0) {
        setHasContent(true);
        nodes.unobserve(observer);
      }
    };
    nodes.observe(observer);
    return () => nodes.unobserve(observer);
  }, [yDoc, binding]);

  const canvasContextValue = useMemo(() => ({ canvasId: entityId }), [entityId]);
  const { remoteStates } = useCanvasAwareness({ awareness, user });
  const { reportPointer, reportSelection } = useCursorAwareness({ awareness });

  const nodeClassName = useCallback((node: { id: string }) => {
    if (!diffResult) return '';
    const cls = diffResult.added.has(node.id) ? styles.diffAdded
      : diffResult.deleted.has(node.id) ? styles.diffDeleted
      : diffResult.moved.has(node.id) ? styles.diffMoved
      : '';
    return cls;
  }, [diffResult]);

  if (hasContent === null) {
    return (
      <Flex align="center" justify="center" className={styles.fill}>
        <Spin size="lg" />
      </Flex>
    );
  }

  if (!hasContent) {
    return (
      <Flex
        data-testid="canvas-viewer-empty"
        vertical
        align="center"
        justify="center"
        gap="sm"
        className={styles.fill}
      >
        <Icon name={getEntityKindMeta('canvas').icon} size={32} className={styles.emptyIcon} />
        <Text className={styles.emptyText}>This canvas is empty</Text>
      </Flex>
    );
  }

  return (
    <CanvasContext.Provider value={canvasContextValue}>
      <Flex className={styles.fillRelative}>
        <EmbeddableCanvas
          doc={yDoc}
          awareness={awareness}
          binding={binding}
          readOnly
          remoteStates={remoteStates}
          reportPointer={reportPointer}
          reportSelection={reportSelection}
          nodeClassName={diffResult ? nodeClassName : undefined}
          fitOnMount={fitOnMount}
          hideControls={hideControls}
        />
        {!hideControls && (
          <Flex
            align="center"
            gap="xs"
            className={styles.viewOnlyBadge}
          >
            <Icon name="visibility" size={12} className={styles.viewOnlyIcon} />
            <Text className={styles.viewOnlyText}>View only</Text>
          </Flex>
        )}
      </Flex>
    </CanvasContext.Provider>
  );
}
